import React, { useEffect } from "react";
import { observer, Observer } from "mobx-react-lite";
import { ErrorEnum, useAppState } from "../../../states/app-state";
import BearerTextField from "../../../components/fields/BearerTextField";
import { getAuth, deleteUser, updateEmail } from "firebase/auth";
import {
  validateEmail,
  validatePassword,
  validateRegistrationEmailValidationCode,
} from "../../../components/validation/FieldsValidation";
import DisabledClickLoading from "../../../components/DisabledClickLoading";
import { Grid, Button, FormHelperText } from "@mui/material";
import { log } from "../../../components/error/error";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
} from "@mui/material";

const SecondStep: React.FC = observer(() => {
  const appState = useAppState();
  const { signUp } = appState.registration;
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [counter, setCounter] = React.useState(240);
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [enterOtpOpen, setEnterOtpOpen] = React.useState(false);
  const [updateEmailModal, setUpdateEmailModal] = React.useState(false);
  const [counterClass, setCounterClass] = React.useState("remaing-time green");
  const url =
    appState.remoteConfigBaseUrl !== null
      ? appState.remoteConfigBaseUrl.rider_onboarding_url
      : "";

  const [showTimer, setShowTimer] = React.useState(true);
  const timerRef = React.useRef<NodeJS.Timeout | null>(null);

  const [resendCodeCounter, setResendCodeCounter] = React.useState(0);
  const [emailAddress, setEmailAddress] = React.useState("");

  useEffect(() => {
    if (enterOtpOpen) {
      startResendCodeCounterTimer();
    } else {
      setResendCodeCounter(0);
    }
  }, [enterOtpOpen]);

  useEffect(() => {
    let timer: any;
    if (resendCodeCounter > 0) {
      timer = setTimeout(
        () => setResendCodeCounter(resendCodeCounter - 1),
        1000
      );
    }
    return () => clearTimeout(timer);
  }, [resendCodeCounter]);

  useEffect(() => {
    if (signUp.email.length < 5) {
      logout();
      return;
    }

    if (counter > 0 && showTimer) {
      timerRef.current = setTimeout(() => {
        setCounter((prev) => prev - 1);
      }, 1000);
    }

    if (counter < 120) {
      setCounterClass("remaing-time orange");
    }
    if (counter < 60) {
      setCounterClass("remaing-time red");
    }
    if (counter === 0) {
      clearTimeout(timerRef.current!);
      signUp.setTimer(false);
      setDialogOpen(true);
    }

    const emailCodeVerification = window.localStorage.getItem(
      "shouldBringEmailValidationCode"
    );
    if (Boolean(emailCodeVerification)) {
      window.localStorage.removeItem("shouldBringEmailValidationCode");
      setEnterOtpOpen(true);
      stopTimer();
      hideTimer();
      handleResendEmailVeificationCode();
    }

    return () => clearTimeout(timerRef.current!);
  }, [counter, showTimer]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const stopTimer = () => {
    clearTimeout(timerRef.current!);
  };

  const hideTimer = () => {
    stopTimer();
    setShowTimer(false);
  };

  const startResendCodeCounterTimer = () => {
    setResendCodeCounter(59);
  };

  const handleResendEmailVeificationCode = async () => {
    try {
      setLoading(true);
      setError("");
      await signUp.onboardingRegisterRequestEmailCode();
      startResendCodeCounterTimer();
      setLoading(false);
    } catch (err: any) {
      log(err);
      setLoading(false);
      setError(err?.message);
    }
  };

  const handleVeifEmailCodeToken = async () => {
    try {
      setLoading(true);
      setError("");
      const result = await signUp.onboardingRegisterConfirmEmailCode();
      if (result) {
        switch (result.code) {
          case 200:
            appState.registration.signUp.setPhoneNumberAuthentication();
            setEnterOtpOpen(false);
            break;
          default:
            appState.setErrorMessage({
              message: result.message || "unknown error",
              error: ErrorEnum.ServerError,
            });
            break;
        }
        setLoading(false);
      }
    } catch (err: any) {
      log(err);
      setLoading(false);
      setError(err?.message);
    }
  };

  const handleClick = () => {
    logout();
  };

  const logout = () => {
    try {
      setLoading(true);
      appState.login.logout();
      window.location.replace(url);
      setLoading(false);
    } catch (err: any) {
      setLoading(false);
    }
  };

  const handleSetPassword = async () => {
    try {
      if (signUp.email.length < 5) {
        logout();
        return;
      }
      setLoading(true);
      setError("");
      await signUp.createNewUser();
      setLoading(false);
      setEnterOtpOpen(true);
      stopTimer();
      hideTimer();
      signUp.setPassword("");
    } catch (err: any) {
      log(err);
      setLoading(false);
    }
  };

  const handleBack = () => {
    appState.registration.signUp.setEmailAuthentication();
  };

  const updateEmailAddress = () => {
    setUpdateEmailModal(true);
    setEnterOtpOpen(false);
    setEmailAddress(signUp.email);
  };

  const handleUpdateEmailAddress = async () => {
    try {
      setLoading(true);
      const auth = getAuth();
      const user = auth.currentUser;
      if (user) {
        await updateEmail(user, signUp.email);
        await signUp.onboardingRegisterRequestEmailCode();
        setUpdateEmailModal(false);
        setEnterOtpOpen(true);
      }
    } catch (err: any) {
      switch (err?.message) {
        case "Firebase: Error (auth/email-already-in-use).":
          appState.setErrorMessage({
            message: "This email address is already in use",
            error: ErrorEnum.Unknown,
          });
          break;
        default:
          appState.setErrorMessage({
            message: err?.message || "unknown error!",
            error: ErrorEnum.RecentLoginError,
          });
          break;
      }
      log(err);
    } finally {
      setLoading(false);
    }
  };

  function secondsToTime(e: number) {
    const m = Math.floor((e % 3600) / 60)
        .toString()
        .padStart(2, "0"),
      s = Math.floor(e % 60)
        .toString()
        .padStart(2, "0");

    return m + ":" + s;
  }

  return (
    <>
      <Grid container spacing={1} sx={{ margin: "8px 0" }}>
        {showTimer && (
          <Grid item sm={12} xs={12}>
            <h3 className={counterClass}>{secondsToTime(counter)}</h3>
          </Grid>
        )}

        <Grid item sm={6} xs={12}>
          <Observer>
            {() => (
              <>
                <BearerTextField
                  sx={{ paddingBottom: "10px" }}
                  label="Password"
                  type="password"
                  value={signUp.password}
                  onChange={(e: any) => signUp.setPassword(e.target.value)}
                  onValidate={(v: any) => signUp.setPasswordValid(v)}
                  validator={validatePassword}
                />
                {error && (
                  <FormHelperText error={!!error}>{error}</FormHelperText>
                )}
              </>
            )}
          </Observer>
        </Grid>
        <Grid item sm={6} xs={12}>
          <Observer>
            {() => (
              <>
                <BearerTextField
                  sx={{ paddingBottom: "10px" }}
                  label="Confirm Password"
                  type="password"
                  value={signUp.confirmPassword}
                  onChange={(e: any) =>
                    signUp.setConfirmPassword(e.target.value)
                  }
                />
                {signUp.password !== signUp.confirmPassword && (
                  <FormHelperText error>
                    Please make sure your Passwords match.
                  </FormHelperText>
                )}
              </>
            )}
          </Observer>
        </Grid>
        <Observer>
          {() => {
            return (
              <>
                <Button
                  variant="contained"
                  sx={{ display: "block", margin: "20px auto 0 auto" }}
                  onClick={handleBack}
                >
                  Back
                </Button>
                {loading && <DisabledClickLoading />}
              </>
            );
          }}
        </Observer>
        <Observer>
          {() => {
            const valid =
              signUp.passwordValid &&
              signUp.password === signUp.confirmPassword;
            return (
              <>
                <Button
                  variant="contained"
                  sx={{ display: "block", margin: "20px auto 0 auto" }}
                  onClick={handleSetPassword}
                  disabled={!valid}
                >
                  Set Password
                </Button>
                {loading && <DisabledClickLoading />}
              </>
            );
          }}
        </Observer>
      </Grid>
      <Dialog open={dialogOpen}>
        <DialogContent>
          <DialogContentText>Session expired</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button color="primary" variant="contained" onClick={handleClick}>
            Ok
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={enterOtpOpen}>
        <DialogContent>
          <DialogContentText>
            Enter the 6-digit code we’ve sent to {signUp.email}
          </DialogContentText>
          <Grid item xs={12}>
            <Observer>
              {() => (
                <BearerTextField
                  sx={{ paddingBottom: "10px" }}
                  label="Verification Code"
                  type="string"
                  style={{ marginTop: 20 }}
                  value={signUp.emailVerificationCode}
                  onChange={(e: any) =>
                    signUp.setEmailVerificationCode(e.target.value)
                  }
                  onValidate={(v: any) =>
                    signUp.setEmailVerificationCodeValid(v)
                  }
                  validator={validateRegistrationEmailValidationCode}
                />
              )}
            </Observer>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            color="primary"
            variant="contained"
            onClick={updateEmailAddress}
          >
            Update Email Address
          </Button>
          <Button
            color="primary"
            variant="contained"
            disabled={resendCodeCounter > 0}
            onClick={handleResendEmailVeificationCode}
            style={{ textTransform: "none" }}
          >
            {resendCodeCounter > 0
              ? `RESEND CODE IN ${resendCodeCounter}s`
              : "RESEND CODE"}
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={handleVeifEmailCodeToken}
            className="verify-email-code"
            disabled={signUp.emailVerificationCodeValid ? false : true}
          >
            Verify
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={updateEmailModal}>
        <DialogContent>
          <DialogContentText>Enter new email</DialogContentText>
          <Grid item xs={12}>
            <Observer>
              {() => (
                <BearerTextField
                  sx={{ paddingBottom: "10px" }}
                  label="Email"
                  type="email"
                  value={signUp.email}
                  onChange={(e: any) => signUp.setEmail(e.target.value)}
                  onValidate={(v: any) => signUp.setRegistrationEmailValid(v)}
                  validator={validateEmail}
                />
              )}
            </Observer>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            color="primary"
            variant="contained"
            onClick={() => {
              signUp.setEmail(emailAddress);
              setUpdateEmailModal(false);
              setEnterOtpOpen(true);
            }}
            disabled={signUp.registrationEmailValid ? false : true}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={handleUpdateEmailAddress}
            className="verify-email-code"
          >
            Update Email
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
});
export default SecondStep;
